import React, { useState } from "react";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { toast } from "../hooks/use-toast";
import { submitLead } from "../lib/lead";
import { Film, Users, TrendingUp, GraduationCap, ArrowRight } from "lucide-react";

const PERKS = [
  { icon: Film, t: "Media That Sells", b: "Cinematic listing films, broker-open events and a 300K+ social audience behind every one of your listings." },
  { icon: TrendingUp, t: "Leads & Opportunity", b: "Buyer and seller opportunities generated by our brand, content and referral network across the DMV." },
  { icon: GraduationCap, t: "Mentorship", b: "Work side by side with top producers, with coaching on pricing, negotiation and winning listings." },
  { icon: Users, t: "Team Culture", b: "A driven, collaborative team where when you win, we all win \u2014 and you never close a deal alone." },
];

const STATS = [
  { v: "300K+", l: "Social Followers" },
  { v: "500+", l: "Families Served" },
  { v: "$200M+", l: "In Residential Sales" },
  { v: "5.0", l: "Client Rating" },
];

const field = "w-full bg-transparent border-0 border-b border-neutral-300 py-3 text-[15px] text-[#1a1a1a] placeholder:text-neutral-400 focus:outline-none focus:border-[#b4936a] transition-colors";
const EMPTY = { firstName: "", lastName: "", phone: "", email: "", license: "Licensed agent", brokerage: "", volume: "", message: "" };

const ApplyForm = () => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.firstName || !f.email || !f.phone) {
      toast({ title: "Please complete required fields", description: "Name, phone and email are required." });
      return;
    }
    setLoading(true);
    try {
      await submitLead({ firstName: f.firstName, lastName: f.lastName, phone: f.phone, email: f.email, message: f.message, source: "Agent Application", details: { "License Status": f.license, "Current Brokerage": f.brokerage || "\u2014", "Last 12 Mo. Volume": f.volume || "\u2014" } });
      toast({ title: "Application received", description: "Thank you! Our leadership team will reach out to set up a conversation." });
      setF(EMPTY);
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or email omer@capitalcornergroup.com." });
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="join-form" className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8 md:p-12 border border-black/[0.06] shadow-[0_30px_70px_rgba(0,0,0,0.06)] reveal">
      <input data-testid="join-first-name" placeholder="First name *" value={f.firstName} onChange={set("firstName")} className={field} />
      <input data-testid="join-last-name" placeholder="Last name" value={f.lastName} onChange={set("lastName")} className={field} />
      <input data-testid="join-phone" placeholder="Phone *" value={f.phone} onChange={set("phone")} className={field} />
      <input data-testid="join-email" type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={field} />
      <select data-testid="join-license" value={f.license} onChange={set("license")} className={field}>
        <option>Licensed agent</option>
        <option>Currently getting licensed</option>
        <option>Not licensed yet</option>
      </select>
      <input data-testid="join-brokerage" placeholder="Current brokerage" value={f.brokerage} onChange={set("brokerage")} className={field} />
      <input data-testid="join-volume" placeholder="Sales volume, last 12 months" value={f.volume} onChange={set("volume")} className={`${field} md:col-span-2`} />
      <textarea data-testid="join-message" rows={3} placeholder="Tell us about your goals" value={f.message} onChange={set("message")} className={`${field} md:col-span-2 resize-none`} />
      <button type="submit" disabled={loading} data-testid="join-submit" className="md:col-span-2 justify-self-start inline-flex items-center gap-2 bg-[#0d0d0d] text-white text-[11px] tracking-luxe uppercase px-12 py-4 hover:bg-[#b4936a] transition-colors disabled:opacity-60">
        {loading ? "Sending\u2026" : <>Submit Application <ArrowRight size={14} /></>}
      </button>
    </form>
  );
};

const JoinPage = () => {
  useReveal();
  return (
    <div className="page-in">
      <section className="relative min-h-[85vh] flex items-end overflow-hidden bg-black">
        <img src="/team/group-1.webp" alt="Capital Corner Group team" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
        <div className="relative max-w-[1300px] w-full mx-auto px-6 md:px-10 pb-20 md:pb-28 text-white">
          <p className="text-[11px] tracking-luxe uppercase text-[#e7c9a3] mb-5">Become an Agent</p>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] max-w-4xl" data-testid="join-title">Build Your Career With Capital Corner</h1>
          <p className="text-white/75 text-[15px] leading-relaxed max-w-2xl mt-6">Join the DMV&rsquo;s #1 media-driven real estate team. We give you the brand, the content and the support to grow faster than you ever could alone.</p>
          <a href="#apply" data-testid="join-apply-cta" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-luxe uppercase border border-white px-8 py-4 hover:bg-white hover:text-black transition-colors">Apply Now <ArrowRight size={14} /></a>
        </div>
      </section>

      <section className="bg-[#0d0d0d] text-white py-16">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.l} className="reveal border-t border-white/15 pt-6">
              <div className="font-serif-display text-4xl md:text-5xl mb-2">{s.v}</div>
              <div className="text-white/50 text-[10px] tracking-luxe uppercase">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-16 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">Why Agents Join Us</p>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light text-[#1a1a1a]">More Than a Brokerage. A Team Built to Win.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PERKS.map(({ icon: Icon, t, b }) => (
              <div key={t} className="reveal-scale bg-white p-8 border border-black/[0.05] hover:-translate-y-1 transition-transform duration-300">
                <Icon size={26} className="text-[#b4936a] mb-6" strokeWidth={1.4} />
                <h3 className="font-serif-display text-2xl text-[#1a1a1a] mb-3">{t}</h3>
                <p className="text-neutral-500 text-[14px] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="bg-white py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">Apply Today</p>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light text-[#1a1a1a]">Let&rsquo;s Grab a Coffee</h2>
            <p className="text-neutral-500 text-[15px] leading-relaxed max-w-xl mx-auto mt-5">Tell us a little about yourself and our leadership team will reach out personally.</p>
          </div>
          <ApplyForm />
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default JoinPage;
